import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, Menu, X } from "lucide-react";
import Logo from "./Logo";
import { useApproxCity } from "@/lib/geo";
import { CITIES } from "@/data/cities";

const LINKS = [
    { to: "/residential", label: "Residential", id: "residential" },
    { to: "/commercial", label: "Commercial", id: "commercial" },
    { to: "/developers", label: "Developers", id: "developers" },
    { to: "/about", label: "About", id: "about" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { slug } = useApproxCity();
    const location = useLocation();
    const detected = slug ? CITIES.find((c) => c.slug === slug) : null;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "glass border-b border-black/5 py-3"
                    : "bg-gradient-to-b from-obsidian/90 via-obsidian/45 to-transparent py-5"
            }`}
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8">
                <Logo />
                <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.id}
                            to={l.to}
                            data-testid={`nav-${l.id}-link`}
                            className={({ isActive }) =>
                                `link-line text-sm tracking-wide transition-colors ${
                                    isActive ? "text-gold" : "text-mist hover:text-ivory"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="flex items-center gap-3">
                    {detected && (
                        <Link
                            to={`/city/${detected.slug}`}
                            data-testid="nav-detected-city"
                            className="hidden items-center gap-1.5 rounded-full border border-black/10 px-3.5 py-1.5 text-xs text-mist transition-colors hover:border-gold/40 hover:text-gold md:flex"
                        >
                            <MapPin className="h-3.5 w-3.5 text-gold" />
                            {detected.name}
                        </Link>
                    )}
                    <button
                        type="button"
                        data-testid="nav-menu-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 text-ivory lg:hidden"
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.3 }}
                        className="glass absolute inset-x-4 top-full mt-2 rounded-2xl p-6 lg:hidden"
                        data-testid="nav-mobile-menu"
                    >
                        <nav className="flex flex-col gap-5" aria-label="Mobile">
                            {LINKS.map((l) => (
                                <NavLink
                                    key={l.id}
                                    to={l.to}
                                    data-testid={`nav-mobile-${l.id}-link`}
                                    className={({ isActive }) =>
                                        `font-serif text-2xl ${
                                            isActive ? "text-gold" : "text-ivory"
                                        }`
                                    }
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
